<template>
    <div ref="mapEl" class="map-container" :class="{ embedded }"></div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import axios from 'axios'
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'
import 'pannellum/build/pannellum.css'
import 'pannellum/build/pannellum.js'
import Map from 'ol/Map'
import View from 'ol/View'
import GeoJSON from 'ol/format/GeoJSON'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import OSM from 'ol/source/OSM'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Icon from 'ol/style/Icon'
import { defaults as defaultControls } from 'ol/control/defaults'
import FullScreen from 'ol/control/FullScreen'
import Select from 'ol/interaction/Select'
import Popup from 'ol-ext/overlay/Popup'
import proj4 from 'proj4'
import { register } from 'ol/proj/proj4'
import { createEmpty, extend, isEmpty, getCenter } from 'ol/extent'
import { getDefaultMapCenter } from '../../../../open-layers/map-defaults'

const PHOTO_ICON_URL = 'https://cdn-icons-png.flaticon.com/512/3687/3687416.png'
const EPSG_31982 = '+proj=utm +zone=22 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'

const props = defineProps({
    matricula: { type: [Number, String], required: true },
    embedded: { type: Boolean, default: false },
})

const mapEl = ref(null)

let mapInstance = null
let popupOverlay = null
let selectInteraction = null
let pannellumViewer = null
let autoOpenTimeout = null
let pannellumInitTimeout = null

const registerProjections = () => {
    proj4.defs('EPSG:31982', EPSG_31982)
    register(proj4)
}

const getPannellumApi = () => {
    if (window.pannellum?.viewer) {
        return window.pannellum
    }
    if (window.libpannellum?.viewer) {
        return window.libpannellum
    }
    return null
}

const destroyPannellumViewer = () => {
    if (pannellumViewer?.destroy) {
        pannellumViewer.destroy()
    }
    pannellumViewer = null
}

const openPopup360 = (coord, url, title, yaw) => {
    if (!popupOverlay) {
        return
    }

    destroyPannellumViewer()

    const viewerId = `v-${Math.floor(Math.random() * 1000)}`
    const containerClass = props.embedded
        ? 'popup-360-container popup-360-container--embedded'
        : 'popup-360-container'
    popupOverlay.show(coord, `
        <div id="${viewerId}" class="${containerClass}"></div>
        <div class="popup-360-title"><b>${title}</b></div>
    `)

    if (pannellumInitTimeout) {
        clearTimeout(pannellumInitTimeout)
    }

    pannellumInitTimeout = setTimeout(() => {
        const api = getPannellumApi()
        if (!api?.viewer) {
            console.error('Pannellum não carregou.')
            return
        }

        pannellumViewer = api.viewer(viewerId, {
            type: 'equirectangular',
            panorama: url,
            yaw,
            autoLoad: true,
            compass: true,
            autoRotate: -1,
            showFullscreenCtrl: true,
        })

        setTimeout(() => pannellumViewer?.resize?.(), 100)
    }, 400)
}

const addPrincipalFeature = (source, principal) => {
    if (!principal?.json_geo) {
        return
    }

    try {
        const feature = new GeoJSON().readFeature(JSON.parse(principal.json_geo), {
            dataProjection: 'EPSG:31982',
            featureProjection: 'EPSG:3857',
        })
        source.addFeature(feature)
    } catch (error) {
        console.error('Erro no lote principal:', error)
    }
}

const addNearbyFeatures = (source, nearby, baseUrl) => {
    if (!Array.isArray(nearby)) {
        return
    }

    nearby.forEach((item, index) => {
        if (!item?.json_geo) {
            return
        }

        try {
            const feature = new GeoJSON().readFeature(JSON.parse(item.json_geo), {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857',
            })
            const url = item.url || `${baseUrl}/${item.name}`
            feature.set('img', url)
            feature.set('azimute', item.graus ? (180 - parseFloat(item.graus)) : 180)
            feature.set('titulo', `Foto: ${item.name}`)
            source.addFeature(feature)
        } catch (error) {
            console.error(`Erro ao processar item ${index}:`, error)
        }
    })
}

const initMap = (data) => {
    const principalSource = new VectorSource()
    const principalLayer = new VectorLayer({
        source: principalSource,
        style: new Style({
            stroke: new Stroke({ color: 'red', width: 4 }),
            fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' }),
        }),
    })

    const nearbySource = new VectorSource()
    const nearbyLayer = new VectorLayer({
        source: nearbySource,
        style: new Style({
            image: new Icon({
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                scale: 0.05,
                src: PHOTO_ICON_URL,
            }),
        }),
    })

    mapInstance = new Map({
        target: mapEl.value,
        controls: defaultControls().extend([
            new FullScreen(),
        ]),
        view: new View({
            zoom: 18,
            center: getDefaultMapCenter(),
        }),
        layers: [
            new TileLayer({ source: new OSM() }),
            principalLayer,
            nearbyLayer,
        ],
    })

    popupOverlay = new Popup({
        popupClass: props.embedded ? 'default anim foto360-popup--embedded' : 'default anim',
        closeBox: true,
        onclose: destroyPannellumViewer,
    })
    mapInstance.addOverlay(popupOverlay)

    const principal = data?.principal ?? null
    const nearby = data?.nearby ?? []
    const baseUrl = data?.foto360BaseUrl ?? ''

    addPrincipalFeature(principalSource, principal)
    addNearbyFeatures(nearbySource, nearby, baseUrl)

    const combinedExtent = createEmpty()
    if (principalSource.getFeatures().length > 0) {
        extend(combinedExtent, principalSource.getExtent())
    }
    if (nearbySource.getFeatures().length > 0) {
        extend(combinedExtent, nearbySource.getExtent())
    }

    if (!isEmpty(combinedExtent)) {
        mapInstance.getView().fit(combinedExtent, {
            padding: [80, 80, 80, 80],
            duration: 1000,
        })

        if (principal?.name) {
            const center = principalSource.getFeatures().length > 0
                ? getCenter(principalSource.getExtent())
                : getCenter(combinedExtent)
            const url = principal.url || `${baseUrl}/${principal.name}`
            const yaw = (principal.graus !== undefined && principal.graus !== null)
                ? (180 - parseFloat(principal.graus))
                : 180

            autoOpenTimeout = setTimeout(() => {
                openPopup360(center, url, 'Foto Principal', yaw)
            }, 1500)
        }
    }

    selectInteraction = new Select({
        layers: [nearbyLayer],
    })
    mapInstance.addInteraction(selectInteraction)

    selectInteraction.on('select', (event) => {
        if (!event.selected.length) {
            return
        }

        const feature = event.selected[0]
        const coord = feature.getGeometry().getCoordinates()
        openPopup360(coord, feature.get('img'), feature.get('titulo'), feature.get('azimute'))
    })
}

const loadData = async () => {
    registerProjections()

    const { data } = await axios.get(`/api/map-actions/foto360/${props.matricula}`)
    initMap(data?.data ?? {})
}

onMounted(loadData)

onUnmounted(() => {
    if (autoOpenTimeout) {
        clearTimeout(autoOpenTimeout)
    }
    if (pannellumInitTimeout) {
        clearTimeout(pannellumInitTimeout)
    }

    destroyPannellumViewer()

    if (selectInteraction && mapInstance) {
        mapInstance.removeInteraction(selectInteraction)
    }

    mapInstance?.setTarget(null)
    mapInstance = null
    popupOverlay = null
    selectInteraction = null
})
</script>

<style scoped>
.map-container {
    width: 100%;
    height: 100vh;
}

.map-container.embedded {
    height: 100%;
    min-height: 400px;
}
</style>

<style>
.ol-popup {
    width: 600px !important;
    min-height: 450px !important;
}

.ol-popup.foto360-popup--embedded {
    width: 360px !important;
    min-height: unset !important;
    max-width: calc(100% - 12px);
    box-sizing: border-box;
}

.popup-360-container {
    width: 100%;
    height: 400px;
    background-color: #000;
}

.popup-360-container--embedded {
    height: 200px;
}

.popup-360-title {
    text-align: center;
    padding: 4px 5px;
    font-size: 12px;
    line-height: 1.2;
}

.ol-popup.foto360-popup--embedded .popup-360-title {
    padding: 2px 4px;
    font-size: 11px;
}

.ol-full-screen {
    top: .5em;
    right: .5em;
}
</style>
